import DashboardChartRangeFilter from "@components/DashboardChartFilters/DashboardChartRangeFilter";
import DashboardDefaultChartEmptyState from "@components/EmptyStates/DashboardDefaultChartEmptyState";
import useCalendarActivityTypes from "@hooks/useCalendarActivityTypes";
import React, { useState } from "react";
import ReactApexChart from "react-apexcharts";
import { sendEventChartUsed } from "@services/dashboardAnalyticEventService";
import { dashboardChartFilterAndActionTypes } from "@utils/constants/dashboard/analyticDashboardEvents";
import { DASHBOARD_CHART_TYPES } from "@utils/constants/dashboard/dashboardModuleConstants";

import DashboardChartFilters from "../../../components/DashboardChartFilters";
import DashboardChartResponsibleFilter from "../../../components/DashboardChartFilters/DashboardChartResponsibleFilter";
import DashboardChartWrapper from "../../../components/DashboardChartWrapper";
import { QueryLoaderWrapper, Skeletons } from "../../../components/Loaders";
import { CustomChartStyleWrapper, ToggletipText, ToggletipTextWrapper } from "../../../style";
import { getChartOptions, MAXIMUM_RESPONSIBLE_NUMBER } from "./helpers";
import useInProgressActivitiesByResponsibleStackedColumnChartQuery from "./useInProgressActivitiesByResponsibleStackedColumnChartQuery";
import useDefaultResponsibleFilter from "src/hooks/useDefaultResponsibleFilter";

const ALLOWED_RANGE_TYPES = [
  "TODAY",
  "THIS_WEEK",
  "THIS_MONTH",
  "NEXT_WEEK",
  "NEXT_MONTH",
] as const;

type InProgressAllowedRangeType = (typeof ALLOWED_RANGE_TYPES)[number];

const InProgressActivitiesByResponsibleStackedColumnChart = () => {
  const DEFAULT_RESPONSIBLE_FILTER = useDefaultResponsibleFilter(MAXIMUM_RESPONSIBLE_NUMBER);
  const [responsibleFilter, setResponsibleFilter] = useState(DEFAULT_RESPONSIBLE_FILTER);
  const [rangeFilter, setRangeFilter] = useState<InProgressAllowedRangeType>("NEXT_WEEK");

  const calendarActivityTypes = useCalendarActivityTypes();

  const [queryStatus, inProgressActivitiesResponse] =
    useInProgressActivitiesByResponsibleStackedColumnChartQuery(
      rangeFilter,
      responsibleFilter,
      calendarActivityTypes
    );
  const { responsible, series, noData } = inProgressActivitiesResponse;
  const options = getChartOptions(responsible, calendarActivityTypes);

  const shouldRenderEmptyStates = noData;

  const setRangeFilterAndSendMixPanelEvent = (filter: InProgressAllowedRangeType) => {
    setRangeFilter(filter);
    sendEventChartUsed(
      DASHBOARD_CHART_TYPES.IN_PROGRESS_ACTIVITIES_BY_RESPONSIBLE,
      dashboardChartFilterAndActionTypes.DATE_RANGE,
      filter
    );
  };

  const setResponsibleFilterAndSendMixPanelEvent = (filter: string[]) => {
    setResponsibleFilter(filter);
    sendEventChartUsed(
      DASHBOARD_CHART_TYPES.IN_PROGRESS_ACTIVITIES_BY_RESPONSIBLE,
      dashboardChartFilterAndActionTypes.RESPONSIBLE
    );
  };

  const CHART_HEIGHT = 450;
  const CHART_OFFSET = 15;

  const header = {
    title: "Atividades a concluir por responsável",
    description:
      "Confira as atividades a concluir no período selecionado para analisar como está a produtividade por responsável e reorganizar as demandas quando necessário.",
  };

  const toggleTip = {
    title: "Atividades a concluir",
    text: (
      <ToggletipTextWrapper>
        <ToggletipText>
          No gráfico, as atividades a concluir são consideradas de acordo com o responsável, ou
          seja, independem dos envolvidos ou de quem as criou.
        </ToggletipText>
        <ToggletipText>Tarefas sem data não são consideradas.</ToggletipText>
      </ToggletipTextWrapper>
    ),
    onClickCallback: () => {
      sendEventChartUsed(
        DASHBOARD_CHART_TYPES.IN_PROGRESS_ACTIVITIES_BY_RESPONSIBLE,
        dashboardChartFilterAndActionTypes.TOGGLE_TIP_OPENED
      );
    },
    callToAction: {
      text: "Acessar central de ajuda",
      callback: () => {
        sendEventChartUsed(
          DASHBOARD_CHART_TYPES.IN_PROGRESS_ACTIVITIES_BY_RESPONSIBLE,
          dashboardChartFilterAndActionTypes.HELP_CENTER_CLICK
        );
        window.open(
          "https://astrea.aurum.com.br/pt-BR/articles/8043389-como-analisar-as-atividades-por-responsavel",
          "_blank"
        );
      },
    },
  };

  return (
    <DashboardChartWrapper
      title={header.title}
      description={header.description}
      toggleTip={toggleTip}
    >
      <DashboardChartFilters>
        <DashboardChartRangeFilter
          allowedRangeTypes={ALLOWED_RANGE_TYPES as Writeable<typeof ALLOWED_RANGE_TYPES>}
          selectedRange={rangeFilter}
          setRangeFunction={setRangeFilterAndSendMixPanelEvent}
        />
        <DashboardChartResponsibleFilter
          currentFilter={responsibleFilter}
          setFilter={setResponsibleFilterAndSendMixPanelEvent}
          maximumSelected={MAXIMUM_RESPONSIBLE_NUMBER}
        />
      </DashboardChartFilters>
      {shouldRenderEmptyStates ? (
        <DashboardDefaultChartEmptyState
          title="Nenhuma atividade a concluir encontrada"
          subtitle="Verifique os filtros selecionados."
          chartId={DASHBOARD_CHART_TYPES.IN_PROGRESS_ACTIVITIES_BY_RESPONSIBLE}
          link="https://astrea.aurum.com.br/pt-BR/articles/8043389-como-analisar-as-atividades-por-responsavel"
        />
      ) : (
        <CustomChartStyleWrapper legendMaxWidth>
          <QueryLoaderWrapper
            queryStatus={queryStatus}
            spinnerConfig={{ height: CHART_HEIGHT, isCounter: false }}
            skeletonComponent={
              <Skeletons.InProgressActivitiesSkeleton height={CHART_HEIGHT + CHART_OFFSET} />
            }
          >
            <ReactApexChart
              options={options}
              series={series}
              type="bar"
              height={CHART_HEIGHT}
              width="95%"
            />
          </QueryLoaderWrapper>
        </CustomChartStyleWrapper>
      )}
    </DashboardChartWrapper>
  );
};

export default InProgressActivitiesByResponsibleStackedColumnChart;
